// Kopplung von Adresszeile und Tabs (docs/shell.md).
//
// Beide Richtungen, mit klarer Zustaendigkeit:
//
//   URL -> Tabs   Jeder Sprung (Link, Zurueck-Taste, navigate()) oeffnet
//                 den Seiten-Tab zu diesem Pfad oder springt zu ihm,
//                 wenn er schon offen ist.
//   Tabs -> URL   Der Tab MIT FOKUS bestimmt, was in der Adresszeile
//                 steht. Ein Wechsel zwischen offenen Tabs ersetzt den
//                 Eintrag (replaceState), er legt keinen neuen an --
//                 sonst laeuft die Zurueck-Taste durch die Klick-
//                 Historie der Tab-Leiste statt durch die Navigation.
//
// Tabs, die keine Seite sind (ein Container, ein Beleg), lassen die
// Adresszeile in Ruhe: sie haben keine Route. Die Adresse zeigt dann
// weiter auf die Seite, von der man gekommen ist -- und genau dorthin
// fuehrt ein Reload dann auch zurueck, zusaetzlich zum gemerkten Stapel.
//
// Einen Platz im VERLAUF bekommen sie trotzdem (seit 07.10.2026; Tim am
// offenen Dokument: "Hier geht es dann nicht mehr zurueck"). Ohne eigenen
// Eintrag sprang die Zurueck-Geste -- Wischen auf dem Trackpad,
// Zurueck-Taste der Maus, Cmd+Pfeil -- am Dokument vorbei auf eine
// fruehere Seite oder ganz aus der App. Jetzt legt das Betreten eines
// solchen Tabs einen Eintrag mit derselben Adresse an (state.shellTab).
//
// "Zurueck" SCHLIESST den Tab und fuehrt auf die Seite darunter (Tim:
// "Wenn man auf zurueck klickt, dann sollte der Tab mit dem Dok auch
// geschlossen werden. Sonst koennte man einfach auf den Vault-Tab
// klicken"). Der Eintrag merkt sich dafuer, WAS der Tab war (typ +
// params): "vor" oeffnet ihn wieder.
//
// Geschlossen wird nur bei genau diesem Schritt: aus dem Eintrag des Tabs
// auf den direkt darunter. Der hat dieselbe Adresse -- so ist der Eintrag
// entstanden. Ein Schritt VOR auf eine spaetere Seite (andere Adresse)
// laesst den Tab offen.

import { tick } from "svelte";
import { navSections, type SectionId } from "../nav-sections";
import { findRedirect } from "../routes";
import { leseHashNeu, router } from "../router.svelte";
import { oeffneSektion } from "./chrome.svelte";
import {
  aktiverTab,
  gehePfadImTab,
  layoutWurdeHergestellt,
  oeffneTab,
  schliesseTabNachId,
  schliesseTabsWo,
  springeZuSeite,
  springeZuTab,
  START_TAB,
  stelleLayoutHer,
  unberuehrterStart,
  warVorschau,
} from "./tabs.svelte";

/** Bereichs-Übersichten (/infra, /zuhause, ...) gibt es am Schreibtisch
 *  nicht mehr: der Baum links zeigt alles (Tim 02.10.2026). Ein Pfad auf
 *  einen Bereich -- Link, Suchtreffer, alter Bookmark -- zeigt dessen
 *  Baum statt einen Tab zu öffnen. Die Seite selbst lebt nur noch auf
 *  dem Telefon (pages/BereichsUebersicht.svelte). */
function bereichVon(pfad: string): SectionId | null {
  const id = navSections.find((s) => s.href === pfad)?.id ?? null;
  return id && MIT_SEITE.has(id) ? null : id;
}

/** Ausnahme: Bereiche, deren Seite mehr zeigt als ihr Baum, öffnen auch
 *  am Schreibtisch als Tab. Claude (Tim 21.10.2026: „Dafür sollten wir
 *  dann hier mal eine Claude Übersichtsseite bauen") -- Tempo je Stunde,
 *  API-Guthaben, Repo-Prüfungen, Repos; geöffnet über „Übersicht →" im
 *  Kopf des Baums. */
const MIT_SEITE = new Set<SectionId>(["claude"]);

function istBereichsTab(t: { typ: string; params: Record<string, unknown> }): boolean {
  return t.typ === "page" && bereichVon(String(t.params.path ?? "")) !== null;
}

/** Wir setzen den Hash auch selbst. Dieses Flag verhindert, dass die
 *  eigene Aenderung als Nutzer-Navigation zurueckkommt. */
let selbstGesetzt = false;

function hashPfad(): string {
  const roh = window.location.hash.replace(/^#/, "");
  const q = roh.indexOf("?");
  const pfad = (q >= 0 ? roh.slice(0, q) : roh) || "/";
  return pfad;
}

/** oeffneAusUrl macht aus dem aktuellen Hash einen Tab. Beachtet die
 *  Redirect-Tabelle, damit alte Bookmarks nach IA-Refactors nicht in
 *  einem 404-Tab landen. */
export function oeffneAusUrl(): void {
  const pfad = hashPfad();

  const bereich = bereichVon(pfad);
  if (bereich) {
    oeffneSektion(bereich);
    if (!aktiverTab()) oeffneTab(START_TAB.typ, { ...START_TAB.params });
    spiegleFokusInUrl();
    return;
  }

  const ziel = findRedirect(pfad);
  if (ziel) {
    // Query-Teil mitnehmen: /tasks?focus=<id> soll als
    // /system/tasks?focus=<id> landen, nicht nackt.
    const roh = window.location.hash.replace(/^#/, "");
    const q = roh.indexOf("?");
    navigiereHart(q >= 0 ? ziel + roh.slice(q) : ziel);
    return;
  }

  // Unbekannte Pfade bekommen trotzdem einen Tab -- die 404-Ansicht
  // gehoert in die Flaeche, nicht in ein Nirgendwo. Ausnahme: ein
  // leerer Hash beim Kaltstart, der ist einfach die Startseite.
  oeffneTab("page", { path: pfad });
}

function navigiereHart(pfad: string): void {
  selbstGesetzt = true;
  window.location.hash = pfad;
  // Der Guard faellt erst, wenn das hashchange-Ereignis durch ist --
  // NICHT im naechsten Microtask.
  //
  // Warum das wichtig ist: hashchange kommt als eigene Aufgabe, also
  // NACH allen Microtasks. Mit queueMicrotask war der Schutz schon
  // wieder offen, wenn das Ereignis eintraf; oeffneAusUrl() hat den
  // gerade geoeffneten Tab ein zweites Mal geoeffnet -- diesmal ohne
  // "vorschau", was ihn sofort wieder anheftete. Ergebnis: Vorschau-
  // Tabs gab es nie (gemessen am 15.08.).
  //
  // Der Timer ist der Ausweg fuer den Fall, dass gar kein hashchange
  // kommt (gleicher Pfad): sonst bliebe der Schutz fuer immer zu.
  clearTimeout(guardTimer);
  guardTimer = setTimeout(() => (selbstGesetzt = false), 60);
}

let guardTimer: ReturnType<typeof setTimeout> | undefined;

/** Der oberste Verlaufs-Eintrag gehoert einem Tab ohne Adresse? */
function tabImVerlauf(): string | null {
  const s = window.history.state as { shellTab?: unknown } | null;
  return typeof s?.shellTab === "string" ? s.shellTab : null;
}

/** Wo der Verlauf zuletzt stand: gehoerte der oberste Eintrag einem Tab
 *  ohne Adresse, und mit welcher Adresse. popstate nennt nur das ZIEL
 *  eines Schritts -- woher er kam, steht nur hier. */
let standTab: string | null = null;
let standAdresse = "";

function merkeStand(): void {
  standTab = tabImVerlauf();
  standAdresse = window.location.hash;
}

/** Laeuft gerade unser eigener Schritt zurueck (siehe unten)? history.back()
 *  wirkt erst spaeter -- ohne diese Sperre naehme ein zweiter Aufruf in
 *  derselben Runde einen zweiten Eintrag mit und verliesse die Seite. */
let ruecksprung = false;
let ruecksprungTimer: ReturnType<typeof setTimeout> | undefined;

/** spiegleFokusInUrl schreibt den Pfad des fokussierten Tabs in die
 *  Adresszeile, ohne einen Verlaufs-Eintrag anzulegen. */
function spiegleFokusInUrl(): void {
  spiegle();
  merkeStand();
}

function spiegle(): void {
  const tab = aktiverTab();
  if (!tab) return;
  if (tab.typ !== "page") {
    // Ein Tab ohne Adresse: eigener Eintrag im Verlauf, gleiche Adresse.
    // Kommt man von einer Seite, entsteht er neu; wechselt man zwischen
    // zwei solchen Tabs, wird er nur umgeschrieben -- sonst liefe die
    // Zurueck-Taste durch jeden Klick in der Tab-Leiste.
    if (ruecksprung) return;
    const oben = tabImVerlauf();
    if (oben === tab.id) return;
    // typ + params als schlichte Kopie: der Tab ist ein Svelte-Proxy, den
    // der Verlauf nicht ablegen kann.
    const eintrag = { shellTab: tab.id, typ: tab.typ, params: $state.snapshot(tab.params) };
    if (oben) window.history.replaceState(eintrag, "");
    else window.history.pushState(eintrag, "");
    return;
  }
  const pfad = String(tab.params.path ?? "/");
  if (pfad === hashPfad()) {
    // Zurueck auf der Seite, von der man kam (Tab-Klick, Kreuz): den
    // Eintrag des verlassenen Tabs wieder vom Verlauf nehmen. Er liegt
    // immer direkt ueber einem Eintrag mit derselben Adresse -- so ist er
    // entstanden --, der Schritt verlaesst die Seite also nicht.
    if (tabImVerlauf() && !ruecksprung) {
      ruecksprung = true;
      clearTimeout(ruecksprungTimer);
      ruecksprungTimer = setTimeout(() => (ruecksprung = false), 400);
      window.history.back();
    }
    return;
  }

  selbstGesetzt = true;
  window.history.replaceState(null, "", "#" + pfad);
  // replaceState feuert kein hashchange -- den Router selbst nachziehen,
  // sonst haengen Breadcrumb und isActive() auf dem alten Pfad.
  router.current = pfad;
  router.query = {};
  queueMicrotask(() => (selbstGesetzt = false));
}

/** starteRouteSync verbindet beide Richtungen. Rueckgabe raeumt auf. */
export function starteRouteSync(): () => void {
  const beiHashwechsel = () => {
    // Eigene Aenderung: einmal schlucken und den Schutz sofort wieder
    // oeffnen -- der naechste Wechsel kommt vom Nutzer.
    merkeStand();
    if (selbstGesetzt) {
      selbstGesetzt = false;
      clearTimeout(guardTimer);
      return;
    }
    // Der Schritt fuehrte in den Eintrag eines Tabs ohne Adresse -- zurueck
    // von einer spaeteren Seite. popstate hat den Tab schon nach vorn
    // geholt. Die Adresse gehoert der Seite darunter; sie hier zu oeffnen,
    // naehme ihm den Platz gleich wieder weg (bis 07.10.2026 landete man so
    // im Vault-Browser statt wieder im Dokument).
    const oben = tabImVerlauf();
    if (oben && aktiverTab()?.id === oben) return;
    oeffneAusUrl();
  };
  // Zurueck/Vor im Verlauf. Aendert sich dabei die Adresse, kommt danach
  // ein hashchange und erledigt es wie immer. Bleibt sie GLEICH -- das ist
  // der Schritt aus einem Tab ohne Adresse heraus oder in ihn hinein --,
  // kommt keines; dann hier.
  const beiPopstate = (e: PopStateEvent) => {
    if (ruecksprung) {
      // Unser eigener Schritt: die Tabs stehen schon. Nur nachziehen, falls
      // inzwischen wieder ein Tab ohne Adresse vorn ist -- der hat
      // waehrend des Schritts keinen Eintrag bekommen.
      ruecksprung = false;
      clearTimeout(ruecksprungTimer);
      spiegleFokusInUrl();
      return;
    }
    const vonTab = standTab;
    const gleicheAdresse = window.location.hash === standAdresse;
    merkeStand();
    const st = e.state as { shellTab?: unknown; typ?: unknown; params?: unknown } | null;
    if (typeof st?.shellTab === "string") {
      // In den Eintrag eines Tabs ohne Adresse. "Zurueck" hat ihn
      // geschlossen -- dann entsteht er aus dem, was der Eintrag sich
      // gemerkt hat, neu.
      let da = springeZuTab(st.shellTab);
      if (!da && typeof st.typ === "string" && st.params && typeof st.params === "object") {
        try {
          oeffneTab(st.typ, st.params as Record<string, unknown>);
          da = true;
        } catch {
          // Den Tab-Typ gibt es nicht mehr.
        }
      }
      if (da) return;
      // Nicht wiederzubekommen (Eintrag aus einer aelteren Fassung): dann
      // zaehlt die Adresse, wie bei einem Seiten-Eintrag.
    } else if (vonTab && gleicheAdresse && aktiverTab()?.id === vonTab) {
      // Ein Seiten-Eintrag. Kam der Schritt aus dem Eintrag eines Tabs ohne
      // Adresse und blieb die Adresse dabei gleich, war es "zurueck" aus
      // diesem Tab: er wird geschlossen. Nur, wenn er auch vorn steht --
      // der Verlauf schliesst nichts, was man gerade nicht ansieht.
      schliesseTabNachId(vonTab);
    }
    // Erst NACH einem moeglichen hashchange nachsehen (es kommt im selben
    // Zug, gleich nach popstate): hat sich die Adresse geaendert, hat der
    // die Seite schon geholt, und hier bleibt nichts.
    setTimeout(() => {
      const tab = aktiverTab();
      const pfad = hashPfad();
      if (tab && tab.typ === "page" && String(tab.params.path ?? "/") === pfad) return;
      if (!springeZuSeite(pfad)) oeffneAusUrl();
    }, 0);
  };
  window.addEventListener("hashchange", beiHashwechsel);
  window.addEventListener("popstate", beiPopstate);
  return () => {
    window.removeEventListener("hashchange", beiHashwechsel);
    window.removeEventListener("popstate", beiPopstate);
  };
}

/** bootLayout stellt den gemerkten Stapel her und sorgt dafuer, dass
 *  am Ende garantiert etwas offen ist:
 *
 *   1. gemerkte Tabs zurueckholen (ueberlebt Reload und Deploy),
 *   2. den Pfad aus der Adresszeile oeffnen (Deeplink gewinnt den
 *      Fokus -- wer einen Link aufmacht, will DAS sehen),
 *   3. ist danach immer noch nichts da, die Startseite oeffnen. */
/** Der Deeplink, mit dem die Sitzung begann -- samt Parametern
 *  (?auf=, ?q=, ?focus=). Nur für holeLayoutNach().
 *
 *  Seiten lesen ihre Parameter einmal und werfen sie dann aus der
 *  Adresse (consumeQueryParam). Auf einem kalten Browser baut
 *  holeLayoutNach() die Tabs danach neu auf: die Seite entsteht ein
 *  zweites Mal, findet nichts mehr vor und zeigt ihren Grundzustand.
 *  Der Verweis „Einstellungen › Passkeys" aus einer Meldung landete so
 *  auf der ungefilterten Liste (gemessen 04.10.2026). */
let startLink: string | null = null;

export function bootLayout(): void {
  stelleLayoutHer();
  // Übersichts-Tabs aus Sitzungen vor dem 02.10.2026 gibt es nicht mehr.
  schliesseTabsWo(istBereichsTab);

  const pfad = hashPfad();
  // War der Pfad in der Adresszeile die Vorschau, die wir gerade
  // verworfen haben, darf er NICHT zurueckkommen -- sonst waere das
  // Verwerfen wirkungslos und der Tab am Ende sogar angeheftet.
  // spiegleFokusInUrl() unten zieht die Adresse dann auf den Tab nach,
  // der wirklich offen ist.
  if (pfad && pfad !== "/" && !warVorschau(pfad)) {
    oeffneAusUrl();
  } else if (!aktiverTab()) {
    oeffneTab(START_TAB.typ, { ...START_TAB.params });
  }

  // Fokus und Adresszeile in Deckung bringen: nach dem Wiederherstellen
  // kann der aktive Tab ein anderer sein als der Pfad im Hash.
  spiegleFokusInUrl();

  // Erst hier merken: eine Weiterleitung hat den Pfad oben schon
  // umgeschrieben, die Seite hat ihre Parameter noch nicht verbraucht.
  const roh = window.location.hash.replace(/^#/, "");
  startLink = roh.includes("?") ? roh : null;
}

/** holeLayoutNach ist der ZWEITE Versuch, sobald der Server-Stand da
 *  ist.
 *
 *  bootLayout() laeuft beim Mount der Shell. Auf einem Browser ohne
 *  warmen localStorage-Cache (neues Geraet, geleerte Website-Daten,
 *  privates Fenster, jeder Headless-Lauf) ist zu diesem Zeitpunkt noch
 *  nichts geladen -- der gemerkte Stapel existiert, wir sehen ihn nur
 *  noch nicht. Statt ihn verloren zu geben, holen wir ihn nach.
 *
 *  Zwei Bedingungen, damit das nie etwas kaputt macht:
 *   - es wurde noch nie etwas hergestellt, und
 *   - der Nutzer hat seither nichts selbst geoeffnet.
 *  Sonst bleibt alles, wie es ist. */
export function holeLayoutNach(): void {
  if (layoutWurdeHergestellt() || !unberuehrterStart()) return;
  if (!stelleLayoutHer()) return;
  schliesseTabsWo(istBereichsTab);

  // Der Deep-Link gewinnt den Fokus -- wie in bootLayout, und aus
  // demselben Grund: wer einen Link aufmacht, will DAS sehen.
  //
  // Ohne diesen Schritt verschluckt die nachgeholte Wiederherstellung
  // ihn: bootLayout() hat auf dem kalten Browser den verlinkten Tab
  // geoeffnet (der einzige, also "unberuehrter Start"), und
  // stelleLayoutHer() ersetzt die Tabs komplett. Man landete auf dem
  // zuletzt aktiven Tab der letzten Sitzung statt dort, wo der Link
  // hinzeigte (gemessen 22.08.).
  const pfad = hashPfad();
  if (pfad && pfad !== "/" && !warVorschau(pfad)) {
    oeffneAusUrl();
  }
  spiegleFokusInUrl();

  // Die Seite des Deeplinks entsteht jetzt neu -- ihr die Parameter
  // zurückgeben, die der erste Aufbau schon verbraucht hat. Erst NACH
  // dem Umbau (tick): vorher läse sie noch einmal die alte Seite, die
  // gleich verschwindet, und die neue bekäme wieder nichts.
  const link = startLink;
  startLink = null;
  if (link && link.slice(0, link.indexOf("?")) === pfad) {
    void tick().then(() => {
      if (hashPfad() !== pfad) return; // inzwischen woanders
      window.history.replaceState(null, "", "#" + link);
      leseHashNeu();
    });
  }
}

/** Nach jeder Fokus-/Tab-Aenderung aufzurufen (ein $effect in der
 *  Shell erledigt das). */
export function haltUrlNach(): void {
  spiegleFokusInUrl();
}

// Fuer den Pfad ueber dem Editor: eine Ebene hoch im SELBEN Tab.
// Klappt das nicht (Ziel schon woanders offen), faellt es auf den
// normalen Weg zurueck -- dann ist der Sprung gewollt.
export function gehePfad(pfad: string): void {
  if (!gehePfadImTab(pfad)) {
    oeffneTab("page", { path: pfad }, {});
  }
  navigiereHart(pfad);
}

/** oeffnePfad ist der Weg fuer Code, der bisher navigate() gerufen hat
 *  und dabei ausdruecklich einen TAB meint (Aktivitaetsleiste,
 *  Befehlspalette, Explorer). Legt bei Bedarf einen Verlaufs-Eintrag
 *  an, damit die Zurueck-Taste den Sprung rueckgaengig machen kann.
 *
 *  Er macht dabei dasselbe wie oeffneAusUrl() -- und das ist kein
 *  Zufall, sondern die Lehre aus dem 20.08.: ein Klick auf den
 *  Such-Treffer "Host Auto-Reboot" landete in einem 404-Tab
 *  ("/tasks?focus=host-auto-reboot&q=Host Auto-Reboot"). Zwei Gruende,
 *  beide hier behoben:
 *
 *   1. Der QUERY-Teil gehoert in die Adresszeile, nicht in die
 *      Tab-Identitaet. findRoute() matcht auf den nackten Pfad; mit
 *      "?focus=..." daran findet es nichts. Ein Tab ist eine SEITE --
 *      welcher Treffer darauf gerade hervorgehoben wird, ist ein
 *      fluechtiges Detail und steht im Hash (router.query).
 *   2. Die REDIRECT-Tabelle galt nur fuer die Richtung URL -> Tab.
 *      Wer von innen sprang, umging sie -- ein Pfad, den es nur noch
 *      als Weiterleitung gibt, wurde so zum toten Tab.
 *
 *  Beides fiel erst nach dem Shell-Umbau auf: vorher navigierte ein
 *  Treffer bloss die Seite, und der Router raeumt beides selbst auf. */
export function oeffnePfad(
  pfad: string,
  opts: { gruppe?: 0 | 1; vorschau?: boolean } = {},
): void {
  const q = pfad.indexOf("?");
  const nackt = q >= 0 ? pfad.slice(0, q) : pfad;
  const query = q >= 0 ? pfad.slice(q) : "";
  const ziel = findRedirect(nackt) ?? nackt;

  const bereich = bereichVon(ziel);
  if (bereich) {
    oeffneSektion(bereich);
    return;
  }

  oeffneTab("page", { path: ziel }, opts);
  navigiereHart(ziel + query);
}
